import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useUserPeppolIntegration from 'hooks/Peppol/useUserPeppolIntegration';
import { useSubscriptionPlan } from 'hooks/Subscriptions';
import useFetchAccessRequests from 'hooks/Access';
import useVerifications from 'hooks/useVerifications';
import _get from 'lodash/get';
import {
  useCallback,
  useReducer,
  type FC,
  type PropsWithChildren,
} from 'react';
import { useLocation } from 'react-router';
import { IntegrationsStatusEnum } from 'types/integrations';
import { AvailableCountries } from 'types/users.types';
import { SettingsViewsEnum } from 'types/settings.types';
import { isSubscriptionTrialPlan } from 'utils/subscriptions.utils';
import { AppBannersContext } from './context';
import { BANNERS_STORAGE_KEYS } from './helpers';
import { isWeb } from 'utils/constants';
import { IdentityProvider } from 'api/v2/users/verifications';

const banners = Object.keys(BANNERS_STORAGE_KEYS) as BANNERS_STORAGE_KEYS[];

const AppBannersProvider: FC<PropsWithChildren<any>> = ({ children }) => {
  const {
    TRIAL_BANNER_DISMISS_KEY,
    BELGIUM_PEPPOL_VERIFICATION_DISMISS_KEY,
    PEPPOL_ACCOUNTANT_REGISTERED_DISMISS_KEY,
  } = BANNERS_STORAGE_KEYS;

  const { data: user } = useGetCurrentCustomerData();
  const pendingAccessAPI = useFetchAccessRequests();
  const location = useLocation();

  const isBelgiumUser = user?.country === AvailableCountries.belgium;

  const plan = useSubscriptionPlan();

  const shouldShowTrialBanner = isSubscriptionTrialPlan(plan?.planInternalName);

  const peppol = useUserPeppolIntegration();
  const verifications = useVerifications();

  const shouldShowPendingAccessRequestsBanner =
    pendingAccessAPI.data?.length &&
    !location.pathname.includes(`/settings/${SettingsViewsEnum.dataAccess}`) &&
    isWeb;

  const [state, dispatch] = useReducer(
    (prev: Record<string, any>, next: Record<string, any>) => ({
      ...prev,
      ...next,
    }),
    banners.reduce((all, key) => {
      const keyName = _get(BANNERS_STORAGE_KEYS, [key]);
      return {
        ...all,
        [keyName]: !!localStorage.getItem(keyName),
      };
    }, {}),
  );

  const dismissBanner = useCallback((dismissKey: BANNERS_STORAGE_KEYS) => {
    dispatch({ [dismissKey]: true });
    localStorage.setItem(dismissKey, 'true');
  }, []);

  return (
    <AppBannersContext.Provider
      value={{
        displayTRIALBanner:
          shouldShowTrialBanner && !state[TRIAL_BANNER_DISMISS_KEY],
        displayBelgiumPeppolVerificationBanner:
          !state[BELGIUM_PEPPOL_VERIFICATION_DISMISS_KEY] &&
          isBelgiumUser &&
          !!peppol.data &&
          !peppol.data.requiresUserVerification &&
          peppol.data.status === IntegrationsStatusEnum.active,
        displayPeppolAccountantRegisteredBanner:
          isWeb &&
          !state[PEPPOL_ACCOUNTANT_REGISTERED_DISMISS_KEY] &&
          !!peppol.data &&
          peppol.data.status === IntegrationsStatusEnum.pending &&
          verifications.data?.identity?.provider === IdentityProvider.Access,
        displayPendingAccessRequestsBanner:
          !!shouldShowPendingAccessRequestsBanner,
        dismissBanner,
      }}
    >
      {children}
    </AppBannersContext.Provider>
  );
};

export default AppBannersProvider;
